/* global CompareUtil */

class ComparisonPage {
  constructor() {
    this.wrapper = document.getElementById('comparison-page-wrapper');
    if (!this.wrapper) return;

    this.loading = this.wrapper.querySelector('.comparison-page-loading');
    this.empty = this.wrapper.querySelector('.comparison-page-empty');
    this.content = this.wrapper.querySelector('.comparison-page-content');
    this.tableBody = document.getElementById('compare-table-body');
    this.productsData = [];
    this.draggedColumnIndex = null;
    this.clearAllButton = this.wrapper.querySelector('.js-clear-compare-page');

    if (this.clearAllButton) {
      this.clearAllButton.addEventListener('click', () => this.clearAll());
    }

    this.init();
  }

  async init() {
    // Get products from localStorage
    const compareProducts = this.getCompareProducts();

    if (!compareProducts || compareProducts.length === 0) {
      this.showEmpty();
      return;
    }

    try {
      await this.renderComparison(compareProducts);
    } catch (error) {
      this.showEmpty();
    }
  }

  getCompareProducts() {
    try {
      // Use CompareUtil if available, otherwise fallback to direct localStorage access
      if (typeof CompareUtil !== 'undefined') {
        return CompareUtil.getSelectedProducts();
      }
      // Fallback: read directly from localStorage with correct key
      const stored = localStorage.getItem('cc-compare-products');
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  }

  getNormalizedProductUrl(storedUrl) {
    if (storedUrl.startsWith('http://') || storedUrl.startsWith('https://')) {
      return storedUrl;
    }
    const localeRoot = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
    const match = storedUrl.match(/(\/products\/[^?#]*)/);
    return match ? localeRoot + match[1] : storedUrl;
  }

  showEmpty() {
    this.loading.classList.add('hidden');
    this.empty.classList.remove('hidden');
    this.content.classList.add('hidden');
  }

  showContent() {
    this.loading.classList.add('hidden');
    this.empty.classList.add('hidden');
    this.content.classList.remove('hidden');
  }

  async renderComparison(compareProducts) {
    // Fetch product data for all products
    const productsData = await this.fetchProductsData(compareProducts);

    if (!productsData || productsData.length === 0) {
      this.showEmpty();
      return;
    }

    // Build comparison table
    this.buildComparisonTable(productsData);

    // Show content
    this.showContent();

    // Bind remove buttons
    this.bindRemoveButtons();
  }

  async fetchProductsData(compareProducts) {
    const productsData = [];

    await Promise.all(
      compareProducts.map(async (product) => {
        try {
          let productUrl = null;
          let fetchSucceeded = false;

          // Try to use stored URL first
          if (product.url && typeof product.url === 'string' && product.url.trim() !== '') {
            productUrl = product.url.split('?')[0].split('#')[0].trim();

            // Ensure URL is absolute or properly relative
            if (!productUrl.startsWith('http://') && !productUrl.startsWith('https://')) {
              if (!productUrl.startsWith('/')) {
                productUrl = '/' + productUrl;
              }
            }

            // Normalize locale prefix so sections API renders in the current locale
            productUrl = this.getNormalizedProductUrl(productUrl);

            // Try multiple fetch approaches for the product URL

            // Approach 1: Try sections API first (works best in production)
            try {
              const response = await fetch(`${productUrl}?sections=product-comparison-data`);

              if (response.ok) {
                const data = await response.json();
                const sectionHtml = data['product-comparison-data'];

                if (sectionHtml) {
                  const parser = new DOMParser();
                  const doc = parser.parseFromString(sectionHtml, 'text/html');

                  const productData = this.extractProductData(doc, product);

                  if (productData && productData.id && productData.title && productData.title !== product.id.toString()) {
                    productsData[product.id] = productData;
                    fetchSucceeded = true;
                  }
                }
              }
            } catch (urlError) {
              // Continue to next approach
            }

            // Approach 2: Try .json endpoint (works better in dev environment)
            if (!fetchSucceeded) {
              try {
                const jsonResponse = await fetch(`${productUrl}.json`);

                if (jsonResponse.ok) {
                  const jsonData = await jsonResponse.json();

                  if (jsonData.product) {
                    const shopifyProduct = jsonData.product;
                    const productData = this.extractProductDataFromJson(shopifyProduct, product);

                    if (productData && productData.id) {
                      productsData[product.id] = productData;
                      fetchSucceeded = true;
                    }
                  }
                }
              } catch (jsonError) {
                // Continue to next approach
              }
            }
          }

          // If stored URL didn't work or is missing, try to get product URL using Shopify APIs
          if (!fetchSucceeded) {
            // Try multiple approaches to get product data by ID

            // Approach 3: Use product recommendations API (might return the product itself)
            try {
              const recommendationsUrl = `/recommendations/products.json?product_id=${product.id}&limit=10`;
              const response = await fetch(recommendationsUrl);

              if (response.ok) {
                const data = await response.json();
                if (data.products && data.products.length > 0) {
                  // Look for our product in the recommendations (sometimes it includes the source product)
                  const shopifyProduct = data.products.find(p => p.id.toString() === product.id.toString());

                  if (shopifyProduct && shopifyProduct.url) {
                    // Found it! Try JSON endpoint first
                    try {
                      const jsonResponse = await fetch(`${shopifyProduct.url}.json`);
                      if (jsonResponse.ok) {
                        const jsonData = await jsonResponse.json();
                        if (jsonData.product) {
                          const productData = this.extractProductDataFromJson(jsonData.product, product);
                          if (productData && productData.id) {
                            productsData[product.id] = productData;
                            fetchSucceeded = true;
                          }
                        }
                      }
                    } catch (e) {
                      // Try sections API as fallback
                      const sectionResponse = await fetch(`${shopifyProduct.url}?sections=product-comparison-data`);
                      if (sectionResponse.ok) {
                        const sectionData = await sectionResponse.json();
                        const sectionHtml = sectionData['product-comparison-data'];
                        if (sectionHtml) {
                          const parser = new DOMParser();
                          const doc = parser.parseFromString(sectionHtml, 'text/html');
                          const productData = this.extractProductData(doc, product);
                          if (productData && productData.id) {
                            productsData[product.id] = productData;
                            fetchSucceeded = true;
                          }
                        }
                      }
                    }
                  }
                }
              }
            } catch (recommendationsError) {
              // Continue to next approach
            }

            // Approach 4: Use search suggest API
            if (!fetchSucceeded) {
              try {
                const searchUrl = `/search/suggest.json?q=${product.id}&resources[type]=product&resources[limit]=50`;
                const response = await fetch(searchUrl);

                if (response.ok) {
                  const data = await response.json();
                  if (data.resources && data.resources.results && data.resources.results.products) {
                    const foundProduct = data.resources.results.products.find(p =>
                      p.id && p.id.toString() === product.id.toString()
                    );

                    if (foundProduct && foundProduct.url) {
                      // Try JSON endpoint first
                      try {
                        const jsonResponse = await fetch(`${foundProduct.url}.json`);
                        if (jsonResponse.ok) {
                          const jsonData = await jsonResponse.json();
                          if (jsonData.product) {
                            const productData = this.extractProductDataFromJson(jsonData.product, product);
                            if (productData && productData.id) {
                              productsData[product.id] = productData;
                              fetchSucceeded = true;
                            }
                          }
                        }
                      } catch (e) {
                        // Try sections API as fallback
                        const sectionResponse = await fetch(`${foundProduct.url}?sections=product-comparison-data`);
                        if (sectionResponse.ok) {
                          const sectionData = await sectionResponse.json();
                          const sectionHtml = sectionData['product-comparison-data'];
                          if (sectionHtml) {
                            const parser = new DOMParser();
                            const doc = parser.parseFromString(sectionHtml, 'text/html');
                            const productData = this.extractProductData(doc, product);
                            if (productData && productData.id) {
                              productsData[product.id] = productData;
                              fetchSucceeded = true;
                            }
                          }
                        }
                      }
                    }
                  }
                }
              } catch (searchError) {
                // Continue to next approach
              }
            }
          }

          // Last resort: create minimal product data so it at least shows up
          if (!fetchSucceeded) {
            productsData[product.id] = {
              id: product.id,
              url: productUrl || `/products/${product.id}`,
              title: `Product ${product.id}`,
              image: null,
              priceHtml: '',
              tagParameters: {},
              available: false,
              variant: null
            };
          }
        } catch (error) {
          // Create fallback product data even on error
          productsData[product.id] = {
            id: product.id,
            url: product.url || `/products/${product.id}`,
            title: `Product ${product.id}`,
            image: null,
            priceHtml: '',
            tagParameters: {},
            available: false,
            variant: null
          };
        }
      })
    );

    // Return products in the order they were added
    const orderedProducts = [];
    compareProducts.forEach((product) => {
      if (productsData[product.id]) {
        orderedProducts.push(productsData[product.id]);
      }
    });

    return orderedProducts;
  }

  extractProductDataFromJson(shopifyProduct, originalProduct) {
    // Extract data from Shopify's product.json format
    if (!shopifyProduct) return null;

    const productUrl = `/products/${shopifyProduct.handle}`;

    // Get first image
    let image = null;
    if (shopifyProduct.images && shopifyProduct.images.length > 0) {
      const firstImage = shopifyProduct.images[0];
      image = {
        src: firstImage.src,
        alt: firstImage.alt || shopifyProduct.title,
        width: firstImage.width,
        height: firstImage.height
      };
    } else if (shopifyProduct.image) {
      image = {
        src: shopifyProduct.image.src,
        alt: shopifyProduct.image.alt || shopifyProduct.title,
        width: shopifyProduct.image.width,
        height: shopifyProduct.image.height
      };
    }

    // Get first variant
    let variant = null;
    if (shopifyProduct.variants && shopifyProduct.variants.length > 0) {
      const firstVariant = shopifyProduct.variants[0];
      variant = {
        id: firstVariant.id,
        available: firstVariant.available
      };
    }

    // Build price HTML (simple version since we don't have the Liquid template)
    let priceHtml = '';
    if (shopifyProduct.variants && shopifyProduct.variants.length > 0) {
      const firstVariant = shopifyProduct.variants[0];
      const price = firstVariant.price;
      const compareAtPrice = firstVariant.compare_at_price;

      // Format price (assuming the price is in cents for most currencies)
      const formatPrice = (cents) => {
        if (!cents) return '';
        // This is a simplified formatter - in production the sections API has proper Liquid formatting
        const amount = parseFloat(cents) / 100;
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'USD'
        }).format(amount);
      };

      if (compareAtPrice && parseFloat(compareAtPrice) > parseFloat(price)) {
        priceHtml = `<span class="price price--on-sale"><span class="price__sale">${formatPrice(price)}</span> <span class="price__compare"><s>${formatPrice(compareAtPrice)}</s></span></span>`;
      } else {
        priceHtml = `<span class="price">${formatPrice(price)}</span>`;
      }
    }

    // Extract tag parameters (similar format to Liquid extraction)
    const tagParameters = {};
    if (shopifyProduct.tags && Array.isArray(shopifyProduct.tags)) {
      const tagPrefix = 'FIL-L';
      const currentLang = (document.documentElement.lang || 'en').split('-')[0];

      shopifyProduct.tags.forEach(tag => {
        if (tag.includes(tagPrefix)) {
          const tagParts = tag.split('-');
          if (tagParts.length >= 5) {
            const tagLang = tagParts[2];
            if (tagLang === currentLang) {
              const paramStart = tag.split('-P-')[1];
              if (paramStart) {
                const paramParts = paramStart.split('-D-');
                if (paramParts.length >= 2) {
                  const paramName = paramParts[0];
                  const paramValue = paramParts[1];
                  tagParameters[paramName] = paramValue;
                }
              }
            }
          }
        }
      });
    }

    return {
      id: shopifyProduct.id || originalProduct.id,
      url: productUrl,
      title: shopifyProduct.title || `Product ${originalProduct.id}`,
      image: image,
      priceHtml: priceHtml,
      tagParameters: tagParameters,
      available: shopifyProduct.available !== undefined ? shopifyProduct.available : true,
      variant: variant,
      tags: shopifyProduct.tags
    };
  }

  extractProductData(doc, product) {
    // Extract JSON data from script tag
    const jsonScript = doc.querySelector('script.product-comparison-json');
    if (!jsonScript) {
      return null;
    }

    let productData;
    try {
      productData = JSON.parse(jsonScript.textContent);
    } catch (e) {
      return null;
    }

    // Validate that we have actual product data (not empty object)
    if (!productData || !productData.id || Object.keys(productData).length === 0) {
      return null;
    }

    // Extract price HTML from the div
    const priceContainer = doc.querySelector('.product-comparison-price');
    const priceHtml = priceContainer ? priceContainer.innerHTML.trim() : '';

    // Always use the URL from the fetched product data (it's the canonical URL from Shopify)
    // Only fallback to stored URL if productData.url is missing
    let productUrl = productData.url;
    if (!productUrl || productUrl.trim() === '') {
      productUrl = product.url;
    }

    // Normalize URL - ensure it's a valid product URL
    if (productUrl && !productUrl.startsWith('http://') && !productUrl.startsWith('https://')) {
      // If URL doesn't start with /, prepend it
      if (!productUrl.startsWith('/')) {
        productUrl = '/' + productUrl;
      }
    }

    // Use title from productData, or fallback to a default
    let productTitle = productData.title;
    if (!productTitle || productTitle.trim() === '' || productTitle === product.id.toString()) {
      // If title is missing or equals ID, use a fallback
      productTitle = `Product ${product.id}`;
    }

    // Get first variant (or first available variant)
    let variant = null;
    if (productData.variants && productData.variants.length > 0) {
      variant = productData.variants[0];
    }

    return {
      id: productData.id || product.id,
      url: productUrl,
      title: productTitle,
      image: productData.image || null,
      priceHtml: priceHtml,
      tagParameters: productData.tagParameters || {},
      available: productData.available !== undefined ? productData.available : true,
      variant: variant,
      tags: productData.tags,
      rawDoc: doc
    };
  }

  extractTagParameters(doc) {
    // Tag parameters are now extracted from JSON in extractProductData
    // This method is kept for backwards compatibility but is no longer used
    // The tagParameters are already in the productData object
    return {};
  }

  buildComparisonTable(productsData) {
    // Store productsData for drag-and-drop reordering
    this.productsData = productsData;

    // Clear existing content
    this.tableBody.innerHTML = '';

    // Create rows for each field
    // Note: Remove row is no longer needed as remove button is now in the title row action bar
    const rows = [
      { key: 'title', label: theme.strings.compare?.modal?.title || 'Product', renderer: this.renderTitleRow.bind(this) },
      { key: 'price', label: 'Price', renderer: this.renderPriceRow.bind(this) },
      { key: 'addToCart', label: '', renderer: this.renderAddToCartRow.bind(this) }
    ];

    rows.forEach(row => {
      const tr = row.renderer(productsData);
      if (tr) {
        this.tableBody.appendChild(tr);
      }
    });

    // Add TAG parameter rows
    this.renderTagParameterRows(productsData);

    // Initialize drag-and-drop for column reordering
    this.initDragAndDrop();
  }

  renderTitleRow(productsData) {
    const tr = document.createElement('tr');

    const th = document.createElement('th');
    th.textContent = theme.strings.compare?.productLabel || theme.strings.compare?.modal?.title || 'Product';
    tr.appendChild(th);

    productsData.forEach((product, index) => {
      const td = document.createElement('td');
      td.className = `product-compare-${product.id}`;
      td.draggable = true;
      td.dataset.columnIndex = index;

      // Create action bar with drag handle and remove button
      const actionBar = document.createElement('div');
      actionBar.className = 'compare-column-actions';

      // Get internationalized tooltip texts
      const dragTooltip = theme.strings.compare?.dragToReorder || 'Drag to reorder';
      const removeTooltip = theme.strings.compare?.removeProduct || 'Remove';

      // Drag handle
      const dragHandle = document.createElement('div');
      dragHandle.className = 'compare-drag-handle';
      dragHandle.setAttribute('data-tooltip', dragTooltip);
      dragHandle.setAttribute('aria-label', dragTooltip);
      dragHandle.innerHTML = '<span class="drag-grip"></span>';
      actionBar.appendChild(dragHandle);

      // Remove button
      const removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.className = 'compare-remove-btn compare-popup__remove-link';
      removeButton.dataset.productId = product.id;
      removeButton.setAttribute('data-tooltip', removeTooltip);
      removeButton.setAttribute('aria-label', removeTooltip);
      removeButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
      actionBar.appendChild(removeButton);

      td.appendChild(actionBar);

      // Create container for title and image
      const container = document.createElement('div');
      container.className = 'compare-popup__product-info';

      // Add title first (on top)
      const titleLink = document.createElement('a');
      titleLink.href = product.url;
      titleLink.className = 'h5 compare-popup__product-title';
      titleLink.textContent = product.title;

      container.appendChild(titleLink);

      // Add image below title
      if (product.image && product.image.src) {
        const imageWrapper = document.createElement('div');
        imageWrapper.className = 'compare-popup__product-image';

        const imageLink = document.createElement('a');
        imageLink.href = product.url;

        const img = document.createElement('img');
        img.src = product.image.src;
        img.alt = product.image.alt || product.title;
        img.loading = 'lazy';
        if (product.image.width) img.width = product.image.width;
        if (product.image.height) img.height = product.image.height;

        imageLink.appendChild(img);
        imageWrapper.appendChild(imageLink);
        container.appendChild(imageWrapper);
      }

      td.appendChild(container);
      tr.appendChild(td);
    });

    return tr;
  }

  renderPriceRow(productsData) {
    const tr = document.createElement('tr');

    const th = document.createElement('th');
    th.textContent = theme.strings.compare?.priceLabel || 'Price';
    tr.appendChild(th);

    productsData.forEach(product => {
      const td = document.createElement('td');
      td.className = `product-compare-${product.id}`;
      if (product.priceHtml && product.priceHtml.trim()) {
        td.innerHTML = product.priceHtml;
      } else {
        td.textContent = '-';
      }
      tr.appendChild(td);
    });

    return tr;
  }

  static tagsSayNotBuyable(data) {
    const raw = data?.tags;
    const tags = Array.isArray(raw) ? raw : (typeof raw === 'string' ? raw.split(',') : null);
    if (!tags) return false;
    return tags.some(tag => String(tag).toLowerCase().trim() === 'not-buyable');
  }

  renderAddToCartRow(productsData) {
    const tr = document.createElement('tr');

    const th = document.createElement('th');
    th.textContent = '';
    tr.appendChild(th);

    productsData.forEach(product => {
      const td = document.createElement('td');
      td.className = `product-compare-${product.id}`;

      // Check if product has a variant and is available
      const hasVariant = product.variant && product.variant.id;
      const isNotBuyable = ComparisonPage.tagsSayNotBuyable(product);
      const isAvailable = product.available
        && (!product.variant || product.variant.available)
        && !isNotBuyable;

      if (hasVariant && isAvailable) {
        // Create form for add to cart
        const form = document.createElement('form');
        form.className = 'compare-popup__add-to-cart-form';
        form.method = 'POST';
        form.action = theme.routes?.cartAdd || '/cart/add.js';

        // Hidden input for variant ID
        const variantInput = document.createElement('input');
        variantInput.type = 'hidden';
        variantInput.name = 'id';
        variantInput.value = product.variant.id;
        form.appendChild(variantInput);

        // Hidden input for quantity
        const quantityInput = document.createElement('input');
        quantityInput.type = 'hidden';
        quantityInput.name = 'quantity';
        quantityInput.value = '1';
        form.appendChild(quantityInput);

        // Add to cart button
        const button = document.createElement('button');
        button.type = 'submit';
        button.className = 'btn btn--primary compare-popup__add-to-cart-btn';
        const addToCartText = theme.strings?.compare?.addToCart || 'Add to cart';
        button.textContent = addToCartText;
        button.setAttribute('data-add-to-cart-text', addToCartText);

        // Handle form submission
        form.addEventListener('submit', async (e) => {
          e.preventDefault();

          // Disable button
          button.disabled = true;
          button.classList.add('is-loading');
          button.textContent = theme.strings?.compare?.addingToCart || 'Adding...';

          try {
            const formData = new FormData(form);
            let sections = 'cart-icon-bubble';
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer) {
              sections += `,${cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
            }

            formData.append('sections_url', window.location.pathname);
            formData.append('sections', sections);

            // Abort a hanging request so the button cannot stay stuck on "Adding..."
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 15000);

            const response = await fetch(theme.routes?.cartAdd || '/cart/add.js', {
              method: 'POST',
              headers: {
                Accept: 'application/javascript',
                'X-Requested-With': 'XMLHttpRequest'
              },
              body: formData,
              signal: controller.signal
            });
            clearTimeout(timeoutId);

            const data = await response.json();

            if (data.status === 422 || data.status === 'error') {
              throw new Error(data.description || data.message || 'Failed to add to cart');
            }

            // Success - refresh cart drawer if available
            if (cartDrawer) {
              cartDrawer.refresh();
            }

            // Update cart count
            if (typeof updateCartCount === 'function') {
              updateCartCount();
            }

            // Show success state
            button.classList.remove('is-loading');
            button.classList.add('is-success');
            button.textContent = theme.strings?.compare?.added || 'Added!';

            // Dispatch cart update event
            document.dispatchEvent(new CustomEvent('on:cart:add', {
              bubbles: true,
              detail: {
                variantId: product.variant.id
              }
            }));

            // Reset button after 2 seconds
            setTimeout(() => {
              button.disabled = false;
              button.classList.remove('is-success');
              button.textContent = button.getAttribute('data-add-to-cart-text');
            }, 2000);

          } catch (error) {
            button.disabled = false;
            button.classList.remove('is-loading');
            button.textContent = button.getAttribute('data-add-to-cart-text');
            const message = (error.name === 'AbortError' || error.name === 'TypeError')
              ? (theme.strings?.cartError || 'Failed to add product to cart')
              : (error.message || 'Failed to add product to cart');
            alert(message);
          }
        });

        form.appendChild(button);
        td.appendChild(form);
      } else {
        // Product not available or no variant
        const unavailableText = document.createElement('span');
        unavailableText.className = 'compare-popup__unavailable';
        unavailableText.textContent = isNotBuyable
          ? (theme.strings?.noLongerSold || 'No longer sold')
          : (theme.strings?.compare?.soldOut || 'Sold out');
        td.appendChild(unavailableText);
      }

      tr.appendChild(td);
    });

    return tr;
  }

  renderRemoveRow(productsData) {
    const tr = document.createElement('tr');

    const th = document.createElement('th');
    th.textContent = '';
    tr.appendChild(th);

    productsData.forEach(product => {
      const td = document.createElement('td');
      td.className = `product-compare-${product.id}`;

      const removeLink = document.createElement('a');
      removeLink.href = '#';
      removeLink.className = 'compare-popup__remove-link link';
      removeLink.dataset.productId = product.id;
      removeLink.textContent = theme.strings.compare?.removeProduct || theme.strings.compare?.modal?.remove_product || 'Remove';

      td.appendChild(removeLink);
      tr.appendChild(td);
    });

    return tr;
  }

  renderTagParameterRows(productsData) {
    // Collect all unique parameter names across all products
    const allParamNames = new Set();

    productsData.forEach((product) => {
      if (product.tagParameters && typeof product.tagParameters === 'object') {
        Object.keys(product.tagParameters).forEach(paramName => {
          allParamNames.add(paramName);
        });
      }
    });

    // Create a row for each parameter
    allParamNames.forEach(paramName => {
      const tr = document.createElement('tr');

      const th = document.createElement('th');
      th.textContent = paramName;
      tr.appendChild(th);

      productsData.forEach(product => {
        const td = document.createElement('td');
        td.className = `product-compare-${product.id}`;

        // Check if product has this parameter
        const paramValue = (product.tagParameters && product.tagParameters[paramName])
          ? product.tagParameters[paramName]
          : '-';
        td.textContent = paramValue;

        tr.appendChild(td);
      });

      this.tableBody.appendChild(tr);
    });
  }

  bindRemoveButtons() {
    const removeButtons = this.content.querySelectorAll('.compare-popup__remove-link');
    removeButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const productId = button.dataset.productId;
        this.removeProduct(productId);
      });
    });
  }

  updateHeaderCounter(count) {
    const headerCounter = document.querySelector('.js-compare-counter-header');
    if (headerCounter) {
      const counterNumber = headerCounter.querySelector('.header__compare-count-number');
      if (counterNumber) counterNumber.innerText = count;
      headerCounter.style.display = count > 0 ? 'block' : 'none';
    }
  }

  clearAll() {
    if (typeof CompareUtil !== 'undefined') {
      CompareUtil.clearCompare(false);
      CompareUtil.updateCompareButtons();
      CompareUtil.updateCompareCounters(0);
    } else {
      localStorage.removeItem('cc-compare-products');
      this.updateHeaderCounter(0);
    }
    this.showEmpty();
  }

  removeProduct(productId) {
    // Remove from localStorage using CompareUtil if available
    if (typeof CompareUtil !== 'undefined') {
      CompareUtil.removeFromCompare(productId, false);
    } else {
      // Fallback: manually remove from localStorage with correct key
      const compareProducts = this.getCompareProducts();
      const filtered = compareProducts.filter(p => p.id !== productId);
      localStorage.setItem('cc-compare-products', JSON.stringify(filtered));
      this.updateHeaderCounter(filtered.length);
    }

    // Remove column from table
    const columns = document.querySelectorAll(`.product-compare-${productId}`);
    columns.forEach(col => col.remove());

    // Check if any products left
    const remaining = this.getCompareProducts();
    if (!remaining || remaining.length === 0) {
      this.showEmpty();
    }
  }

  // Drag-and-drop methods for column reordering
  initDragAndDrop() {
    const firstRow = this.tableBody.querySelector('tr:first-child');
    if (!firstRow) return;

    const cells = firstRow.querySelectorAll('td[draggable="true"]');
    cells.forEach((cell, index) => {
      cell.addEventListener('dragstart', (e) => this.handleDragStart(e, index));
      cell.addEventListener('dragend', (e) => this.handleDragEnd(e));
      cell.addEventListener('dragover', (e) => this.handleDragOver(e));
      cell.addEventListener('dragenter', (e) => this.handleDragEnter(e));
      cell.addEventListener('dragleave', (e) => this.handleDragLeave(e));
      cell.addEventListener('drop', (e) => this.handleDrop(e, index));
    });
  }

  handleDragStart(e, index) {
    this.draggedColumnIndex = index;
    e.target.classList.add('dragging');
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());

    // Add dragging class to all cells in the column
    const productId = this.productsData[index]?.id;
    if (productId) {
      document.querySelectorAll(`.product-compare-${productId}`).forEach(cell => {
        cell.classList.add('column-dragging');
      });
    }
  }

  handleDragEnd(e) {
    e.target.classList.remove('dragging');

    // Remove dragging class from all cells
    document.querySelectorAll('.column-dragging').forEach(cell => {
      cell.classList.remove('column-dragging');
    });
    document.querySelectorAll('.drag-over').forEach(cell => {
      cell.classList.remove('drag-over');
    });

    this.draggedColumnIndex = null;
  }

  handleDragOver(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }

  handleDragEnter(e) {
    e.preventDefault();
    const cell = e.target.closest('td');
    if (cell && !cell.classList.contains('dragging')) {
      // Add highlight to entire column
      const cellIndex = Array.from(cell.parentNode.children).indexOf(cell) - 1; // -1 for th
      if (cellIndex >= 0 && cellIndex < this.productsData.length) {
        const productId = this.productsData[cellIndex]?.id;
        if (productId) {
          document.querySelectorAll(`.product-compare-${productId}`).forEach(c => {
            c.classList.add('drag-over');
          });
        }
      }
    }
  }

  handleDragLeave(e) {
    const cell = e.target.closest('td');
    if (cell) {
      const relatedTarget = e.relatedTarget?.closest('td');
      if (relatedTarget !== cell) {
        // Remove highlight from column
        const cellIndex = Array.from(cell.parentNode.children).indexOf(cell) - 1;
        if (cellIndex >= 0 && cellIndex < this.productsData.length) {
          const productId = this.productsData[cellIndex]?.id;
          if (productId) {
            document.querySelectorAll(`.product-compare-${productId}`).forEach(c => {
              c.classList.remove('drag-over');
            });
          }
        }
      }
    }
  }

  handleDrop(e, targetIndex) {
    e.preventDefault();

    const sourceIndex = this.draggedColumnIndex;
    if (sourceIndex === null || sourceIndex === targetIndex) return;

    this.swapColumns(sourceIndex, targetIndex);
  }

  swapColumns(fromIndex, toIndex) {
    // Swap in productsData array
    const temp = this.productsData[fromIndex];
    this.productsData.splice(fromIndex, 1);
    this.productsData.splice(toIndex, 0, temp);

    // Swap td elements in all rows
    const rows = this.tableBody.querySelectorAll('tr');
    rows.forEach(row => {
      const cells = Array.from(row.children);
      // cells[0] is the th, so product cells start at index 1
      const fromCell = cells[fromIndex + 1];
      const toCell = cells[toIndex + 1];

      if (fromCell && toCell) {
        if (fromIndex < toIndex) {
          // Moving right: insert after target
          toCell.parentNode.insertBefore(fromCell, toCell.nextSibling);
        } else {
          // Moving left: insert before target
          toCell.parentNode.insertBefore(fromCell, toCell);
        }
      }
    });

    // Persist new order to localStorage
    this.persistColumnOrder();

    // Reinitialize drag-and-drop with new positions
    this.initDragAndDrop();
  }

  persistColumnOrder() {
    // Build the new order array for localStorage
    const newOrder = this.productsData.map(product => ({
      id: product.id.toString(),
      url: product.url
    }));

    // Save to localStorage
    if (typeof CompareUtil !== 'undefined') {
      CompareUtil.setSelectedProducts(newOrder);
    } else {
      localStorage.setItem('cc-compare-products', JSON.stringify(newOrder));
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ComparisonPage();
  });
} else {
  new ComparisonPage();
}

